/** Segment rules (crm-email's internal/email/segment) in plain language, and conversion from the
 *  contacts-list filter so a saved search can become an email audience. */
import type { ContactFilter, SegmentDefinition, SegmentRule } from "./types";
import { brandById } from "./brands";
import { addDays, formatCents, formatDate } from "./format";

export const RULE_TYPES: { value: SegmentRule["type"]; label: string; help: string }[] = [
  { value: "tag", label: "Has tags", help: "Current tags: any, all or none of them, optionally added within dates." },
  { value: "tag_event", label: "Tag added or removed", help: "From tag history, between dates." },
  { value: "brand", label: "University and subscription", help: "In a brand, optionally subscribed or unsubscribed to its email." },
  { value: "engagement", label: "Recently active", help: "Opened, clicked, bought, signed in, filled a form or visited within N days." },
  { value: "email_activity", label: "Did something with an email", help: "Opened, clicked, didn't open… a specific send." },
  { value: "purchase", label: "Bought something", help: "Paid orders: any product, a brand, dates, minimum total." },
  { value: "subscription", label: "Has a subscription", help: "Active, trialing or past due." },
  { value: "access", label: "Has access", help: "Investor's Club, Manager Training…" },
  { value: "field", label: "A field", help: "Name, company, email, city, state, lead source, custom fields…" },
  { value: "date", label: "A date", help: "Added, birthday, last order, custom dates…" },
  { value: "utm", label: "Came from a campaign (UTM)", help: "First or last touch source, medium, campaign." },
  { value: "email_status", label: "Email address status", help: "Mailable, bounced, opted out…" },
  { value: "affiliate", label: "Referred by an affiliate", help: "Any or specific affiliates." },
  { value: "segment", label: "In another segment", help: "Reuse a saved segment." },
  { value: "contact_ids", label: "Specific people", help: "A list of contact IDs." },
  { value: "group", label: "A group of rules", help: "Combine rules with any/all inside one rule." },
];

export const ENGAGEMENT_EVENTS: { value: string; label: string }[] = [
  { value: "any", label: "did anything" },
  { value: "email", label: "opened or clicked an email" },
  { value: "email_open", label: "opened an email" },
  { value: "email_click", label: "clicked an email" },
  { value: "purchase", label: "bought something" },
  { value: "login", label: "signed in to a site" },
  { value: "form", label: "filled in a form" },
  { value: "ga4", label: "visited a site" },
  { value: "signup", label: "was added" },
];

export const ACTIVITIES: { value: string; label: string }[] = [
  { value: "opened", label: "opened" },
  { value: "human_opened", label: "opened (people, not machines)" },
  { value: "clicked", label: "clicked" },
  { value: "not_opened", label: "didn't open" },
  { value: "not_clicked", label: "didn't click" },
  { value: "delivered", label: "was delivered" },
  { value: "sent", label: "was sent" },
  { value: "bounced", label: "bounced" },
  { value: "unsubscribed", label: "unsubscribed" },
  { value: "complained", label: "marked it as spam" },
];

export const TEXT_FIELDS: { value: string; label: string }[] = [
  { value: "first_name", label: "First name" },
  { value: "last_name", label: "Last name" },
  { value: "nickname", label: "Nickname" },
  { value: "company", label: "Company" },
  { value: "job_title", label: "Job title" },
  { value: "email", label: "Email address" },
  { value: "city", label: "City" },
  { value: "region", label: "State" },
  { value: "postal_code", label: "ZIP" },
  { value: "lead_source", label: "Lead source" },
  { value: "landing_page", label: "Landing page" },
  { value: "referrer", label: "Referrer" },
  { value: "time_zone", label: "Time zone" },
];

export const TEXT_OPS: { value: string; label: string; noValue?: boolean }[] = [
  { value: "eq", label: "is" },
  { value: "neq", label: "is not" },
  { value: "contains", label: "contains" },
  { value: "not_contains", label: "doesn't contain" },
  { value: "starts_with", label: "starts with" },
  { value: "empty", label: "is empty", noValue: true },
  { value: "not_empty", label: "is filled in", noValue: true },
  { value: "gt", label: "is more than" },
  { value: "lt", label: "is less than" },
];

export const DATE_FIELDS: { value: string; label: string }[] = [
  { value: "created_at", label: "Added" },
  { value: "last_order_at", label: "Last order" },
  { value: "birthday", label: "Birthday" },
  { value: "first_touch_at", label: "First touch" },
  { value: "last_touch_at", label: "Last touch" },
];

export const DATE_OPS: { value: string; label: string; needs: "date" | "two" | "days" | "month" | "none" }[] = [
  { value: "after", label: "is after", needs: "date" },
  { value: "before", label: "is before", needs: "date" },
  { value: "between", label: "is between", needs: "two" },
  { value: "within_last_days", label: "is within the last … days", needs: "days" },
  { value: "more_than_days_ago", label: "is more than … days ago", needs: "days" },
  { value: "month_is", label: "falls in month (1-12)", needs: "month" },
  { value: "empty", label: "is empty", needs: "none" },
  { value: "not_empty", label: "is filled in", needs: "none" },
];

export const EMAIL_STATUSES = ["ok", "unconfirmed", "opted_out", "bounced", "complained", "invalid", "suppressed"];

/** A one-line plain-language description of a rule. */
export function describeRule(r: SegmentRule, tagName: (id: number) => string, segmentName: (id: number) => string = (id) => `segment #${id}`): string {
  const not = r.not ? "NOT: " : "";
  const tags = (ids?: number[]) => (ids ?? []).map(tagName).join(", ") || "(no tags picked)";
  const range = (a?: string, b?: string) => (a && b ? ` between ${formatDate(a)} and ${formatDate(b)}` : a ? ` since ${formatDate(a)}` : b ? ` before ${formatDate(b)}` : "");
  switch (r.type) {
    case "tag":
      return `${not}has ${r.tag_match === "all" ? "all of" : r.tag_match === "none" ? "none of" : "any of"} ${tags(r.tag_ids)}${range(r.applied_since, r.applied_before)}`;
    case "tag_event":
      return `${not}had ${tags(r.tag_ids)} ${r.action === "removed" ? "removed" : "added"}${range(r.since, r.before)}`;
    case "brand":
      return `${not}in ${(r.brand_ids ?? []).map((b) => brandById(b)?.short ?? b).join(" or ") || "any brand"}${r.marketing_status ? `, ${r.marketing_status}` : ""}`;
    case "engagement":
      return `${not}${r.has === false ? "did NOT" : ""} ${ENGAGEMENT_EVENTS.find((e) => e.value === (r.event ?? "any"))?.label ?? r.event} in the last ${r.within_days ?? 90} days`.trim();
    case "email_activity":
      return `${not}${ACTIVITIES.find((a) => a.value === r.activity)?.label ?? r.activity} ${r.report_unit_id ? `email report #${r.report_unit_id}` : `broadcast #${r.send_id}`}`;
    case "purchase":
      return `${not}${r.has === false ? "never bought" : "bought"}${r.product_ids?.length ? ` product ${r.product_ids.join(", ")}` : " anything"}${r.brand_id ? ` from ${brandById(r.brand_id)?.short}` : ""}${r.min_total_cents ? ` for ${formatCents(r.min_total_cents)}+` : ""}${range(r.since, r.before)}`;
    case "subscription":
      return `${not}${r.has === false ? "has no" : "has a"} subscription${r.statuses?.length ? ` (${r.statuses.join(", ")})` : ""}`;
    case "access":
      return `${not}${r.has === false ? "doesn't have" : "has"} access to ${(r.access_keys ?? []).join(", ") || "anything"}`;
    case "field": {
      const op = TEXT_OPS.find((o) => o.value === r.op);
      return `${not}${TEXT_FIELDS.find((f) => f.value === r.field)?.label ?? r.field} ${op?.label ?? r.op}${op?.noValue ? "" : ` "${String(r.value ?? "")}"`}`;
    }
    case "date": {
      const op = DATE_OPS.find((o) => o.value === r.op);
      const v = op?.needs === "days" ? ` ${r.days ?? r.value}` : op?.needs === "two" ? ` ${String(r.value ?? "")} and ${String(r.value2 ?? "")}` : op?.needs === "none" ? "" : ` ${String(r.value ?? "")}`;
      return `${not}${DATE_FIELDS.find((f) => f.value === r.field)?.label ?? r.field} ${op?.label.replace(" … days", "") ?? r.op}${v}`;
    }
    case "utm":
      return `${not}${r.touch ?? "any"} touch UTM${r.source ? ` source ${r.source}` : ""}${r.medium ? ` medium ${r.medium}` : ""}${r.campaign ? ` campaign ${r.campaign}` : ""}`;
    case "email_status":
      return `${not}has an address that is ${(r.statuses ?? []).join(" or ")}`;
    case "affiliate":
      return `${not}${r.has === false ? "not referred" : "referred"} by ${r.affiliate_ids?.length ? `affiliate ${r.affiliate_ids.join(", ")}` : "any affiliate"}`;
    case "segment":
      return `${not}in ${r.segment_id ? segmentName(r.segment_id) : "(pick a segment)"}`;
    case "contact_ids":
      return `${not}one of ${r.contact_ids?.length ?? 0} people`;
    case "group":
      return `${not}${r.match === "any" ? "any" : "all"} of ${r.rules?.length ?? 0} rules`;
  }
}

/** Contacts-list filter -> segment rules. Returns the rules plus the parts that can't carry over. */
export function filterToSegment(f: ContactFilter): { definition: SegmentDefinition; dropped: string[] } {
  const rules: SegmentRule[] = [];
  const dropped: string[] = [];
  const tagsAny = [...(f.tags_any ?? [])];
  if (f.tag_id) rules.push({ type: "tag", tag_ids: [f.tag_id], tag_match: "all" });
  if (tagsAny.length) rules.push({ type: "tag", tag_ids: tagsAny, tag_match: "any" });
  if (f.tags_all?.length) rules.push({ type: "tag", tag_ids: f.tags_all, tag_match: "all" });
  if (f.tags_none?.length) rules.push({ type: "tag", tag_ids: f.tags_none, tag_match: "none" });
  if (f.brand_id || f.subscribed) rules.push({ type: "brand", brand_ids: f.brand_id ? [f.brand_id] : [1, 2, 3, 4, 5], ...(f.subscribed ? { marketing_status: "subscribed" as const } : {}) });
  if (f.email_status === "mailable") rules.push({ type: "email_status", statuses: ["ok"] });
  else if (f.email_status === "no_email") rules.push({ type: "email_status", statuses: EMAIL_STATUSES, not: true });
  else if (f.email_status) rules.push({ type: "email_status", statuses: [f.email_status] });
  if (f.customer === "yes") rules.push({ type: "purchase", has: true });
  if (f.customer === "no") rules.push({ type: "purchase", has: false });
  if (f.access) rules.push({ type: "access", access_keys: [f.access], has: true });
  // The list's dates include the day picked; the segment's "after"/"before" exclude it.
  if (f.created_since) rules.push({ type: "date", field: "created_at", op: "after", value: addDays(f.created_since, -1) });
  if (f.created_until) rules.push({ type: "date", field: "created_at", op: "before", value: addDays(f.created_until, 1) });
  if (f.last_order_since) rules.push({ type: "date", field: "last_order_at", op: "after", value: addDays(f.last_order_since, -1) });
  if (f.last_order_until) rules.push({ type: "date", field: "last_order_at", op: "before", value: addDays(f.last_order_until, 1) });
  if (f.lead_source) rules.push({ type: "field", field: "lead_source", op: "eq", value: f.lead_source });
  if (f.utm_source || f.utm_campaign) rules.push({ type: "utm", touch: "any", ...(f.utm_source ? { source: f.utm_source } : {}), ...(f.utm_campaign ? { campaign: f.utm_campaign } : {}) });
  if (f.affiliate_id) rules.push({ type: "affiliate", affiliate_ids: [f.affiliate_id], has: true });
  if (f.q) dropped.push(`text search "${f.q}"`);
  if (f.ltv_min_cents || f.ltv_max_cents) dropped.push("lifetime value");
  if (f.archived) dropped.push("archive (archived contacts are never emailed)");
  return { definition: { match: "all", rules }, dropped };
}
